import React from 'react';
import { f0, fc } from '../../utils';

/**
 * CashFlowForecastCard — day-by-day timeline of projected paychecks (from
 * Income Sources) and upcoming bills over the forecast window, with a
 * running balance starting from the user's current bank balance.
 * Pure display; receives the pre-computed forecast object from
 * getCashFlowForecast() via useDashboardData.
 */
export function CashFlowForecastCard({ forecast }) {
  if (!forecast || forecast.timeline.length === 0) return null;

  const { timeline, totalProjectedIncome, totalProjectedBills, days, hasBankBalance, lowestPoint, willGoNegative } = forecast;

  const fmtDate = (d) =>
    new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

  const fmtSigned = (n) => (n >= 0 ? f0(n) : '−' + f0(Math.abs(n)));

  return (
    <div className="card cp" style={{ marginBottom: 12 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 10 }}>
        <div
          style={{
            fontSize: 11,
            fontWeight: 700,
            color: 'var(--mut)',
            textTransform: 'uppercase',
            letterSpacing: '.5px',
          }}
        >
          🔮 Cash Flow Forecast — Next {days} Days
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 10 }}>
        <div style={{ padding: '8px 10px', background: 'var(--bg)', border: '1px solid var(--bdr)', borderRadius: 8 }}>
          <div style={{ fontSize: 9, color: 'var(--mut)' }}>💰 Potential Income</div>
          <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--grn)' }}>{f0(totalProjectedIncome)}</div>
        </div>
        <div style={{ padding: '8px 10px', background: 'var(--bg)', border: '1px solid var(--bdr)', borderRadius: 8 }}>
          <div style={{ fontSize: 9, color: 'var(--mut)' }}>📋 Bills Due</div>
          <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--red)' }}>{f0(totalProjectedBills)}</div>
        </div>
      </div>

      {!hasBankBalance && (
        <div
          style={{
            padding: '8px 10px',
            borderRadius: 8,
            fontSize: 11,
            color: 'var(--mut)',
            background: 'var(--surf2)',
            border: '1px solid var(--bdr)',
            marginBottom: 10,
          }}
        >
          💡 Add your bank balance in Plan → Bank to see a projected running balance, not just totals.
        </div>
      )}

      {hasBankBalance && willGoNegative && (
        <div
          style={{
            padding: '8px 10px',
            borderRadius: 8,
            fontSize: 11,
            color: 'var(--red)',
            background: 'rgba(255,107,107,.08)',
            border: '1px solid rgba(255,107,107,.2)',
            marginBottom: 10,
          }}
        >
          ⚠️ Balance is projected to go negative — lowest point is {fmtSigned(lowestPoint.balanceAfter)} around{' '}
          {fmtDate(lowestPoint.date)}.
        </div>
      )}

      <div style={{ maxHeight: 320, overflowY: 'auto' }}>
        {timeline.map((e, i) => (
          <div
            key={`${e.kind}-${e.id || e.sourceId}-${i}`}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '7px 0',
              borderBottom: i < timeline.length - 1 ? '1px solid var(--bdr)' : 'none',
            }}
          >
            <div style={{ width: 44, fontSize: 10, color: 'var(--mut)', fontWeight: 600 }}>{fmtDate(e.date)}</div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 12, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {e.kind === 'income' ? '💵 ' : '📋 '}
                {e.name}
              </div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: e.kind === 'income' ? 'var(--grn)' : 'var(--red)' }}>
                {e.kind === 'income' ? '+' : '−'}
                {fc(e.amt)}
              </div>
              {hasBankBalance && (
                <div style={{ fontSize: 9, color: e.balanceAfter < 0 ? 'var(--red)' : 'var(--mut)' }}>
                  bal {fmtSigned(e.balanceAfter)}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
